import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";
import { Users, KeyRound, PlayCircle, CheckCircle2, XCircle, RefreshCw, AlertTriangle } from "lucide-react";

const CARDS = [
  { key: "total_nurses", label: "Total Nurses", icon: Users, link: "/admin/nurses" },
  { key: "pending_access", label: "Pending Access", icon: KeyRound, link: "/admin/access-requests" },
  { key: "in_progress", label: "Training In Progress", icon: PlayCircle, link: "/admin/nurses" },
  { key: "completed", label: "Training Completed", icon: CheckCircle2, link: "/admin/nurses" },
  { key: "failed", label: "Failed Assessments", icon: XCircle, link: "/admin/grading" },
  { key: "reattempts", label: "Reattempt Requests", icon: RefreshCw, link: "/admin/reattempts" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <Loader />;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-[32px] font-bold text-[#1c1b1b]">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <button key={c.key} data-testid={`kpi-${c.key}`} onClick={() => navigate(c.link)}
              className="bg-white border border-[#E5E7EB] rounded-lg p-5 text-left hover:border-[#003d9b] transition-colors">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[12px] font-bold uppercase tracking-wider text-[#434654]">{c.label}</span>
                <Icon className="w-4 h-4 text-[#003d9b]" />
              </div>
              <p className="text-[48px] font-bold text-[#1c1b1b] leading-none">{data.kpis[c.key]}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h2 className="font-semibold text-[#1c1b1b] mb-4">Monthly Training Completion</h2>
          {data.monthly_completion.length === 0 ? (
            <p className="text-sm text-[#434654] py-12 text-center">No completions recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.monthly_completion}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#434654" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#434654" }} />
                <Tooltip />
                <Bar dataKey="count" fill="#003d9b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
          <h2 className="font-semibold text-[#1c1b1b] mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#F59E0B]" /> Requires Action
          </h2>
          {data.requires_action.length === 0 ? (
            <p className="text-sm text-[#434654]" data-testid="no-actions">All clear. Nothing needs attention.</p>
          ) : (
            <div className="space-y-2" data-testid="requires-action-list">
              {data.requires_action.map((a, i) => (
                <button key={i} data-testid={`action-item-${i}`} onClick={() => navigate(a.link)}
                  className="w-full text-left text-sm font-medium text-[#1c1b1b] bg-[#fcf9f8] border border-[#E5E7EB] rounded-lg px-4 py-3 hover:border-[#003d9b] transition-colors">
                  {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
